import { type FormControlProps } from '@mui/base'
import { type FC } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { PrescriptionPrescriptionTypeEnum } from '@redteclab/api/clients/bully'

import { FormControl, FormLabel, Option, Select } from 'base-ui'

import { PRODUCT_TEMPLATES } from '../../model/productTemplates'
import { useProductPageContext } from '../productPageContext'

import { ProductPrescriptionNfcInfo } from './ProductPrescriptionNfcInfo'

const getPrescriptionTypeOptions = (
  i18n: IntlShape,
  productTemplate: PRODUCT_TEMPLATES,
): { text: string; value: PrescriptionPrescriptionTypeEnum }[] => {
  const { OTC, OTX, RX } = PRODUCT_TEMPLATES

  const PRESCRIPTION_TYPES_OPTIONS_OTC = [
    {
      text: i18n.formatMessage({ id: 'prescription.options.noreceipt' }),
      value: PrescriptionPrescriptionTypeEnum.NoPrescription,
    },
  ]

  const PRESCRIPTION_TYPES_OPTIONS_RX = [
    {
      text: i18n.formatMessage({ id: 'prescription.options.insurance' }),
      value: PrescriptionPrescriptionTypeEnum.InsurancePrescription,
    },
    {
      text: i18n.formatMessage({
        id: 'prescription.options.privatePrescription',
      }),
      value: PrescriptionPrescriptionTypeEnum.PrivatePrescription,
    },
    {
      text: i18n.formatMessage({ id: 'prescription.options.copaymentExempt' }),
      value: PrescriptionPrescriptionTypeEnum.FreePrescription,
    },
  ]

  const PRESCRIPTION_TYPES_OPTIONS_OTX = [
    ...PRESCRIPTION_TYPES_OPTIONS_OTC,
    ...PRESCRIPTION_TYPES_OPTIONS_RX,
  ]

  switch (productTemplate) {
    case OTC: {
      return PRESCRIPTION_TYPES_OPTIONS_OTC
    }
    case OTX: {
      return PRESCRIPTION_TYPES_OPTIONS_OTX
    }
    case RX: {
      return PRESCRIPTION_TYPES_OPTIONS_RX
    }
    default: {
      return []
    }
  }
}

export const ProductPrescriptionTypeSelect: FC<FormControlProps> = (props) => {
  const {
    currentPrescriptionType,
    productTemplate,
    setCurrentPrescriptionType,
  } = useProductPageContext()
  const i18n = useIntl()

  const prescriptionOptions = getPrescriptionTypeOptions(i18n, productTemplate)

  return (
    <FormControl {...props} value={currentPrescriptionType}>
      <FormLabel
        htmlFor="product__prescription-type-select"
        id="product__prescription-type-select-label"
      >
        <FormattedMessage id="product.prescription.type" />
      </FormLabel>
      <Select
        aria-labelledby="product__prescription-type-select-label"
        data-qa-id="prescription-type-select"
        id="product__prescription-type-select"
        onChange={(event, value): void => {
          if (value) {
            setCurrentPrescriptionType(value)
          }
        }}
        slotProps={{
          listbox: {
            title: i18n.formatMessage({ id: 'rxSelectModal.title' }),
          },
          popup: {
            /*
             * Select could be used inside collapsible component, and it breaks UI
             * Better to move this option in place where we see that component is
             * inside collapsible
             */
            disablePortal: false,

            /*
             * Needed for selenium test https://github.com/RedTecLab/xs-qa/blob/14c4deca57a28833aa42c73cb733d2d865e50d46/src/test/functional/desktop/cart/RX034_AppropriatePricesForEachPrescriptionType.groovy#L54
             * But would be better to avoid such cases and find another way to test the case
             */
            keepMounted: true,
          },
        }}
        value={currentPrescriptionType}
      >
        {prescriptionOptions.map(({ text, value }) => (
          <Option
            data-qa-id={`prescription-type-select-${value}`}
            data-qa-value={value}
            key={value}
            value={value}
          >
            {text}
          </Option>
        ))}
        <ProductPrescriptionNfcInfo />
      </Select>
    </FormControl>
  )
}
